import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Chip,
  Stack,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { Creature, CreatureTrait } from '../../types';
import { ConfirmActionDialog } from '../shared/ConfirmActionDialog';
import { SelectWeaponModal } from './SelectWeaponModal';
import { SelectArmourModal } from './SelectArmourModal';
import { SelectCreatureTreasureItemModal } from './SelectCreatureTreasureItemModal';
import { SelectCreatureTraitModal } from './SelectCreatureTraitModal';
import { formatSoldierType } from '../../utils/formatting';

type ViewMode = 'list' | 'form';

const SOLDIER_TYPES = ['StandardSoldier', 'SpecialistSoldier', 'Creature'] as const;

interface FormState {
  name: string;
  type: string;
  move: string;
  fight: string;
  shoot: string;
  armour: string;
  will: string;
  health: string;
  cost: string;
  itemSlot: string;
  notes: string;
  canCarryTreasure: boolean;
  selectedWeaponIds: string[];
  selectedArmourIds: string[];
  selectedTreasureItemIds: string[];
  traits: CreatureTrait[];
}

const emptyForm = (): FormState => ({
  name: '',
  type: 'StandardSoldier',
  move: '6',
  fight: '2',
  shoot: '0',
  armour: '10',
  will: '4',
  health: '14',
  cost: '0',
  itemSlot: '1',
  notes: '',
  canCarryTreasure: true,
  selectedWeaponIds: [],
  selectedArmourIds: [],
  selectedTreasureItemIds: [],
  traits: [],
});

function creatureToForm(c: Creature): FormState {
  return {
    name: c.name,
    type: c.type,
    move: String(c.move),
    fight: String(c.fight),
    shoot: String(c.shoot),
    armour: String(c.armour),
    will: String(c.will),
    health: String(c.health),
    cost: String(c.cost),
    itemSlot: String(c.itemSlot),
    notes: c.notes ?? '',
    canCarryTreasure: c.canCarryTreasure != null && c.canCarryTreasure > 0,
    selectedWeaponIds: c.weapons.map((w) => w.id),
    selectedArmourIds: c.armourItems.map((a) => a.id),
    selectedTreasureItemIds: c.treasureItems.map((t) => t.id),
    traits: c.traits.map((t) => ({ ...t })),
  };
}

function parseNum(val: string, fallback = 0): number {
  const n = parseInt(val, 10);
  return Number.isNaN(n) ? fallback : n;
}

export const ManageCustomBestiaryModal: React.FC = () => {
  const { ui, closeCustomBestiaryModal } = useWarbandStore();
  const {
    customCreatures,
    weapons,
    armours,
    treasureItems,
    addCustomCreature,
    updateCustomCreature,
    deleteCustomCreature,
  } = useReferenceDataStore();

  const [view, setView] = React.useState<ViewMode>('list');
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<FormState>(emptyForm());
  const [deleteTarget, setDeleteTarget] = React.useState<Creature | null>(null);
  const [nameError, setNameError] = React.useState('');

  // Sub-modal open states
  const [weaponModalOpen, setWeaponModalOpen] = React.useState(false);
  const [armourModalOpen, setArmourModalOpen] = React.useState(false);
  const [itemModalOpen, setItemModalOpen] = React.useState(false);
  const [traitModalOpen, setTraitModalOpen] = React.useState(false);

  const handleClose = () => {
    closeCustomBestiaryModal();
    setView('list');
    setEditingId(null);
    setForm(emptyForm());
    setNameError('');
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setNameError('');
    setView('form');
  };

  const openEdit = (creature: Creature) => {
    setEditingId(creature.id);
    setForm(creatureToForm(creature));
    setNameError('');
    setView('form');
  };

  const handleBackToList = () => {
    setView('list');
    setEditingId(null);
    setForm(emptyForm());
    setNameError('');
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      setNameError('Name is required');
      return;
    }
    setNameError('');

    const weaponObjects = weapons.filter((w) => form.selectedWeaponIds.includes(w.id));
    const armourObjects = armours.filter((a) => form.selectedArmourIds.includes(a.id));
    const itemObjects = treasureItems.filter((t) => form.selectedTreasureItemIds.includes(t.id));

    const data: Omit<Creature, 'id' | 'isCustom'> = {
      name: form.name.trim(),
      type: form.type,
      move: parseNum(form.move, 6),
      fight: parseNum(form.fight, 2),
      shoot: parseNum(form.shoot, 0),
      armour: parseNum(form.armour, 10),
      will: parseNum(form.will, 4),
      health: parseNum(form.health, 14),
      cost: parseNum(form.cost, 0),
      itemSlot: parseNum(form.itemSlot, 1),
      notes: form.notes.trim() || null,
      canCarryTreasure: form.canCarryTreasure ? 1 : null,
      weapons: weaponObjects,
      armourItems: armourObjects,
      treasureItems: itemObjects,
      traits: form.traits.filter((t) => t.name.trim()),
    };

    if (editingId) {
      updateCustomCreature(editingId, data);
    } else {
      addCustomCreature(data);
    }

    setView('list');
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteCustomCreature(deleteTarget.id);
    setDeleteTarget(null);
  };

  const removeWeapon = (id: string) =>
    setForm((f) => ({ ...f, selectedWeaponIds: f.selectedWeaponIds.filter((x) => x !== id) }));

  const removeArmour = (id: string) =>
    setForm((f) => ({ ...f, selectedArmourIds: f.selectedArmourIds.filter((x) => x !== id) }));

  const removeTreasureItem = (id: string) =>
    setForm((f) => ({ ...f, selectedTreasureItemIds: f.selectedTreasureItemIds.filter((x) => x !== id) }));

  const removeTrait = (index: number) =>
    setForm((f) => ({ ...f, traits: f.traits.filter((_, i) => i !== index) }));

  return (
    <>
      <Dialog
        open={ui.customBestiaryModalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* ── LIST VIEW ── */}
        {view === 'list' && (
          <>
            <DialogTitle>Custom Bestiary</DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto', flex: 1 }}>
              <Stack direction="row" alignItems="flex-end" justifyContent="flex-end">
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                  Add Creature
                </Button>
              </Stack>
              {customCreatures.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography>No custom creatures yet.</Typography>
                  <Typography variant="body2">Click "Add Creature" to create one.</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {customCreatures.map((creature, idx) => (
                    <React.Fragment key={creature.id}>
                      {idx > 0 && <Divider />}
                      <ListItem
                        disablePadding
                        sx={{ py: 1, pr: 10 }}
                        secondaryAction={
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              aria-label={`Edit ${creature.name}`}
                              onClick={() => openEdit(creature)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label={`Delete ${creature.name}`}
                              color="error"
                              onClick={() => setDeleteTarget(creature)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={creature.name}
                          secondary={
                            <Stack
                              component="span"
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                component="span"
                                label={formatSoldierType(creature.type)}
                                size="small"
                              />
                              <Typography component="span" variant="caption" color="text.secondary">
                                M{creature.move} F{creature.fight}+ S{creature.shoot}+ A{creature.armour} W{creature.will}+ H{creature.health} · {creature.cost}gc
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={handleClose}>Close</Button>
            </DialogActions>
          </>
        )}

        {/* ── FORM VIEW ── */}
        {view === 'form' && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={handleBackToList} aria-label="Back to list">
                  <ArrowBackIcon />
                </IconButton>
                <span>{editingId ? 'Edit Creature' : 'New Creature'}</span>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto', flex: 1 }}>
              <Stack spacing={2.5}>
                {/* Name + Type */}
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  error={!!nameError}
                  helperText={nameError}
                  fullWidth
                  required
                  autoFocus
                />
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={form.type}
                    label="Type"
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                  >
                    {SOLDIER_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {formatSoldierType(t)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Stats */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Stats</Typography>
                  <Grid container spacing={1.5}>
                    {(['move', 'fight', 'shoot', 'armour', 'will', 'health'] as const).map((stat) => (
                      <Grid size={{ xs: 4 }} key={stat}>
                        <TextField
                          label={stat.charAt(0).toUpperCase() + stat.slice(1)}
                          type="number"
                          slotProps={{ htmlInput: { min: 0 } }}
                          value={form[stat]}
                          onChange={(e) => setForm((f) => ({ ...f, [stat]: e.target.value }))}
                          fullWidth
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Cost, Item Slots, Carry Treasure */}
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Cost (gc)"
                      type="number"
                      slotProps={{ htmlInput: { min: 0 } }}
                      value={form.cost}
                      onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Item Slots"
                      type="number"
                      slotProps={{ htmlInput: { min: 0 } }}
                      value={form.itemSlot}
                      onChange={(e) => setForm((f) => ({ ...f, itemSlot: e.target.value }))}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.canCarryTreasure}
                          onChange={(e) => setForm((f) => ({ ...f, canCarryTreasure: e.target.checked }))}
                          size="small"
                        />
                      }
                      label="Carry Treasure"
                      sx={{ m: 0 }}
                    />
                  </Grid>
                </Grid>

                {/* Notes */}
                <TextField
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={2}
                />

                {/* Weapons */}
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Starting Weapons</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setWeaponModalOpen(true)}
                    >
                      Add Weapon
                    </Button>
                  </Stack>
                  {form.selectedWeaponIds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {form.selectedWeaponIds.map((id) => {
                        const w = weapons.find((x) => x.id === id);
                        return w ? (
                          <Chip
                            key={id}
                            label={w.name}
                            size="small"
                            onDelete={() => removeWeapon(id)}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                </Box>

                {/* Armour */}
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Starting Armour</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setArmourModalOpen(true)}
                    >
                      Add Armour
                    </Button>
                  </Stack>
                  {form.selectedArmourIds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {form.selectedArmourIds.map((id) => {
                        const a = armours.find((x) => x.id === id);
                        return a ? (
                          <Chip
                            key={id}
                            label={a.name}
                            size="small"
                            onDelete={() => removeArmour(id)}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                </Box>

                {/* Items */}
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Starting Items</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setItemModalOpen(true)}
                    >
                      Add Item
                    </Button>
                  </Stack>
                  {form.selectedTreasureItemIds.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">None</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {form.selectedTreasureItemIds.map((id) => {
                        const t = treasureItems.find((x) => x.id === id);
                        return t ? (
                          <Chip
                            key={id}
                            label={t.name}
                            size="small"
                            onDelete={() => removeTreasureItem(id)}
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                </Box>

                {/* Traits */}
                <Box>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2">Traits</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setTraitModalOpen(true)}
                    >
                      Add Trait
                    </Button>
                  </Stack>
                  {form.traits.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No traits.</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {form.traits.map((trait, idx) => (
                        <Chip
                          key={trait.id}
                          label={trait.name}
                          size="small"
                          title={trait.description ?? undefined}
                          onDelete={() => removeTrait(idx)}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleBackToList}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}>
                {editingId ? 'Save Changes' : 'Create Creature'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmActionDialog
        open={deleteTarget !== null}
        title="Delete Creature"
        message={`Delete "${deleteTarget?.name ?? ''}" from your custom bestiary? This won't affect existing warband members.`}
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Weapon picker */}
      <SelectWeaponModal
        open={weaponModalOpen}
        memberName="creature"
        excludeWeaponIds={form.selectedWeaponIds}
        onClose={() => setWeaponModalOpen(false)}
        onSelect={(w) => {
          setForm((f) => ({ ...f, selectedWeaponIds: [...f.selectedWeaponIds, w.id] }));
          setWeaponModalOpen(false);
        }}
      />

      {/* Armour picker */}
      <SelectArmourModal
        open={armourModalOpen}
        excludeArmourIds={form.selectedArmourIds}
        onClose={() => setArmourModalOpen(false)}
        onSelect={(a) => {
          setForm((f) => ({ ...f, selectedArmourIds: [...f.selectedArmourIds, a.id] }));
          setArmourModalOpen(false);
        }}
      />

      {/* Item picker */}
      <SelectCreatureTreasureItemModal
        open={itemModalOpen}
        excludeItemIds={form.selectedTreasureItemIds}
        onClose={() => setItemModalOpen(false)}
        onSelect={(t) => {
          setForm((f) => ({ ...f, selectedTreasureItemIds: [...f.selectedTreasureItemIds, t.id] }));
          setItemModalOpen(false);
        }}
      />

      {/* Trait picker */}
      <SelectCreatureTraitModal
        open={traitModalOpen}
        excludeTraitNames={form.traits.map((t) => t.name)}
        onClose={() => setTraitModalOpen(false)}
        onSelect={(trait) => {
          setForm((f) => ({ ...f, traits: [...f.traits, trait] }));
          setTraitModalOpen(false);
        }}
      />
    </>
  );
};
